import React, { useState } from "react";

import {
	AddTodoItem,
	Badge,
	Button,
	Card,
	Checkbox,
	ConfirmModal,
	Input,
	NumberInput,
	Picto,
	TextArea,
	Title,
	TodoList,
	TTodoItem,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { TReportFormProject, TReportTaskItem } from "@/types";

import {
	MAX_BULK_TASK_LINES,
	parseTaskTicket,
	tasksToText,
	textToLines,
	textToTasks,
} from "@/utils";

type TTasksMode = "list" | "text";

/** Switching back to the list asks first when the text dropped at least this many tasks, and more than half of them. */
const CONFIRM_DROPPED_MIN = 3;

interface IReportProjectCardProps {
	project: TReportFormProject;
	index: number;
	isFinalized: boolean;
	setFieldValue: (field: string, value: unknown) => void;
}

export const ReportProjectCard: React.FC<IReportProjectCardProps> = ({
	project,
	index,
	isFinalized,
	setFieldValue,
}) => {
	const [editingName, setEditingName] = useState(false);
	const [expanded, setExpanded] = useState(true);
	// Bulk edit: the tasks as one text, a line each. The text itself lives
	// here — rebuilt from the tasks, a blank line being typed would vanish
	// on the next keystroke — while the tasks are written to the form on
	// every change, so a preview or a save in text mode sees them.
	const [mode, setMode] = useState<TTasksMode>("list");
	const [text, setText] = useState("");
	// The tasks as they were when the text opened: what "undo" restores.
	const [snapshot, setSnapshot] = useState<TReportTaskItem[]>([]);
	const [confirmingList, setConfirmingList] = useState(false);

	const tasks = project.tasks ?? [];
	const ticketPrefixes = project.ticketPrefixes ?? [];

	const field = (name: string) => `projects.${index}.${name}`;

	const todoItems: TTodoItem[] = tasks.map((task) => {
		const { ticket, text } = parseTaskTicket(task.text, ticketPrefixes);
		return {
			id: task.id,
			text,
			// Uppercased for display only — the stored text keeps it as typed.
			before: ticket ? (
				<Badge color="neutral" size="sm">
					{ticket.toUpperCase()}
				</Badge>
			) : undefined,
		};
	});

	const handleTasksChange = (nextItems: TTodoItem[]) => {
		// The ticket goes back exactly where and how it was typed ("[CHR-1] ",
		// " (chr-1)"...): a reorder or an edit of the text must not rewrite it.
		const parsedById = new Map(
			tasks.map((task) => [
				task.id,
				parseTaskTicket(task.text, ticketPrefixes),
			])
		);
		const nextTasks: TReportTaskItem[] = nextItems.map((item) => {
			const parsed = parsedById.get(item.id);
			return {
				id: item.id,
				text: parsed
					? `${parsed.prefix}${item.text}${parsed.suffix}`
					: item.text,
			};
		});
		setFieldValue(field("tasks"), nextTasks);
	};

	const handleAddTask = (text: string) => {
		setFieldValue(field("tasks"), [
			...tasks,
			{ id: crypto.randomUUID(), text },
		]);
	};

	const openText = () => {
		setSnapshot(tasks);
		setText(tasksToText(tasks));
		setMode("text");
	};

	const handleTextChange = (value: string) => {
		setText(value);
		setFieldValue(field("tasks"), textToTasks(value, tasks));
	};

	const undoText = () => {
		setText(tasksToText(snapshot));
		setFieldValue(field("tasks"), snapshot);
	};

	const keptIds = new Set(tasks.map((task) => task.id));
	const dropped = snapshot.filter((task) => !keptIds.has(task.id)).length;
	const textChanged = mode === "text" && text !== tasksToText(snapshot);
	const overCap =
		mode === "text" && textToLines(text).length > MAX_BULK_TASK_LINES;

	const closeText = () => {
		setConfirmingList(false);
		setMode("list");
	};

	// A select-all + delete is one keystroke away in a textarea: losing
	// most of the list asks before it's left behind.
	const backToList = () => {
		if (dropped >= CONFIRM_DROPPED_MIN && dropped > snapshot.length / 2)
			setConfirmingList(true);
		else closeText();
	};

	return (
		<Card elevation={1} bordered>
			<div className="flex items-center gap-3">
				{/* Finalized, every section shown is in the report: nothing
				    left to tick, and the days are a figure, not a field. */}
				{!isFinalized && (
					<Checkbox
						name={field("selected")}
						checked={project.selected}
						onChange={(checked) =>
							setFieldValue(field("selected"), checked)
						}
					/>
				)}
				{project.color && (
					<span
						className="h-2.5 w-2.5 shrink-0 rounded-full"
						style={{ background: project.color }}
					/>
				)}
				{!editingName ? (
					<div className="flex min-w-0 flex-1 items-center gap-2">
						<Title size="h4" className="truncate">
							{project.name}
						</Title>
						{!isFinalized && (
							<Picto
								icon="pen"
								className="w-4 text-black"
								onClick={() => setEditingName(true)}
							/>
						)}
					</div>
				) : (
					<div className="flex min-w-0 flex-1 items-center gap-2">
						<Input
							className="w-80! max-w-full"
							name={field("name")}
							value={project.name ?? ""}
							onChange={(value) =>
								setFieldValue(field("name"), value)
							}
							placeholder={t("projects.namePlaceholder")}
							size="sm"
						/>
						<Picto
							icon="check"
							className="bg-primary-600 h-8 w-8 rounded-md p-1 text-white"
							onClick={() => setEditingName(false)}
						/>
					</div>
				)}
				{isFinalized && (
					<Badge
						color="primary"
						variant="tint"
						size="sm"
						className="shrink-0"
					>
						{t("reports.days.count", {
							count: parseFloat(project.days || "0"),
							value: parseFloat(project.days || "0"),
						})}
					</Badge>
				)}
			</div>

			{!isFinalized && (
				<div className="mt-3 flex items-center gap-3">
					{project.workTime && (
						<span className="shrink-0 text-sm text-neutral-400">
							{project.workTime}
						</span>
					)}
					<NumberInput
						name={field("days")}
						value={project.days ? parseFloat(project.days) : null}
						wrapperClassName="shrink-0 w-24"
						decimals={2}
						size="sm"
						min={0}
						max={365}
						step={0.01}
						after={t("reports.days.daysShort")}
						onBlur={(e) => {
							if (!e.target.value)
								setFieldValue(field("days"), "0");
						}}
						onChange={(value) => {
							if (value === null) {
								setFieldValue(field("days"), "");
								return;
							}
							setFieldValue(
								field("days"),
								value < 0
									? "0"
									: value > 365
										? "365"
										: value.toString()
							);
						}}
					/>
				</div>
			)}

			{/* Several people on the project: each one's share, at their own
			    rhythm and rate - the reader sees why the days add up as they do. */}
			{(project.members?.length ?? 0) > 1 && (
				<ul
					className="mt-2 flex flex-col gap-0.5 text-xs text-neutral-500"
					data-testid="report-project-members"
				>
					{project.members?.map((member) => (
						<li key={member.userId} className="flex gap-3">
							<span className="font-medium text-neutral-700">
								{member.name}
							</span>
							<span>{member.workTime}</span>
							{member.days !== null && (
								<span>
									{t("reports.days.count", {
										count: member.days,
										value: member.days,
									})}
								</span>
							)}
							{member.rate !== null && (
								<span>{member.rate} €/j</span>
							)}
						</li>
					))}
				</ul>
			)}

			<div className="mt-3">
				<div className="flex items-center justify-between gap-2">
					<button
						type="button"
						className="flex items-center gap-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-800"
						onClick={() => setExpanded((value) => !value)}
					>
						<Picto icon="chevron" rotation={expanded ? 270 : 90} />
						{t("tasks.count", { count: tasks.length })}
					</button>
					{expanded && !isFinalized && (
						<div
							role="group"
							aria-label={t("reports.bulkEdit.mode")}
							className="flex gap-1"
						>
							<Button
								size="sm"
								variant={mode === "list" ? "solid" : "ghost"}
								color="neutral"
								aria-pressed={mode === "list"}
								onClick={() => mode === "text" && backToList()}
							>
								{t("reports.bulkEdit.list")}
							</Button>
							<Button
								size="sm"
								variant={mode === "text" ? "solid" : "ghost"}
								color="neutral"
								aria-pressed={mode === "text"}
								onClick={() => mode === "list" && openText()}
							>
								{t("reports.bulkEdit.text")}
							</Button>
						</div>
					)}
				</div>

				{expanded && mode === "text" && !isFinalized && (
					<div className="mt-2 flex flex-col gap-2">
						<TextArea
							aria-label={t("reports.bulkEdit.text")}
							value={text}
							onChange={handleTextChange}
							autoGrow
							rows={6}
							maxRows={24}
							hideError
						/>
						<div className="flex items-start justify-between gap-3 text-xs text-neutral-500">
							{overCap ? (
								<span className="text-red-600" role="alert">
									{t("reports.bulkEdit.overCap", {
										max: MAX_BULK_TASK_LINES,
									})}
								</span>
							) : (
								<span>{t("reports.bulkEdit.hint")}</span>
							)}
							<Button
								size="sm"
								variant="link"
								color="neutral"
								disabled={!textChanged}
								onClick={undoText}
							>
								{t("reports.bulkEdit.undo")}
							</Button>
						</div>
					</div>
				)}

				{expanded && (mode === "list" || isFinalized) && (
					<div className="mt-2">
						<TodoList
							items={todoItems}
							onChange={handleTasksChange}
							editable={!isFinalized}
							removable={!isFinalized}
							reorderable={!isFinalized}
							size="sm"
						/>
						{!isFinalized && (
							<AddTodoItem
								onAdd={handleAddTask}
								placeholder={t("tasks.namePlaceholder")}
								buttonLabel={t("actions.add")}
								size="sm"
								className="mt-3"
							/>
						)}
					</div>
				)}
			</div>

			<ConfirmModal
				open={confirmingList}
				onClose={() => setConfirmingList(false)}
				onConfirm={closeText}
				title={t("reports.bulkEdit.confirmTitle")}
				confirmText={t("reports.bulkEdit.confirm")}
				cancelText={t("reports.bulkEdit.keepEditing")}
				confirmColor="danger"
			>
				{t("reports.bulkEdit.confirmMessage", {
					count: dropped,
					total: snapshot.length,
				})}
			</ConfirmModal>
		</Card>
	);
};
